import { ArrowLeft, Camera, Upload, X } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/avatar";
import { useNavigate } from "react-router-dom";
import { useState, useRef } from "react";
import { toast } from "@/projects/Admin_Bite_Balance/bite-balance/src/hooks/use-toast";

const UploadProfilePhoto = () => {
  const navigate = useNavigate();
  const userProfile = JSON.parse(localStorage.getItem("userProfile") || "{}");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [profileImage, setProfileImage] = useState(
    localStorage.getItem("profileImage") || ""
  );

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "Error",
          description: "Image size should be less than 5MB",
          variant: "destructive",
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setProfileImage(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleRemovePhoto = () => {
    setProfileImage("");
  };

  const handleSaveChanges = () => {
    localStorage.setItem("profileImage", profileImage);
    toast({
      title: "Success",
      description: "Profile photo updated successfully!",
    });
    navigate("/profile/edit");
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/profile/edit")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">Change Photo</h1>
          <div className="w-10" />
        </div>

        {/* Current Profile Photo */}
        <Card className="rounded-2xl border-0 p-6 shadow-sm">
          <div className="flex flex-col items-center">
            <Avatar className="mb-4 h-32 w-32 border-4 border-primary/10">
              {profileImage ? (
                <AvatarImage src={profileImage} alt="Profile" />
              ) : (
                <AvatarFallback className="to-chart-1 bg-gradient-to-br from-primary text-4xl font-bold text-white">
                  {userProfile.name
                    ?.split(" ")
                    .map((n: string) => n[0])
                    .join("") || "RS"}
                </AvatarFallback>
              )}
            </Avatar>
          </div>
        </Card>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageUpload}
        />

        {/* Take Photo Option */}
        <Card
          className="cursor-pointer rounded-2xl border-0 p-5 shadow-sm transition-colors hover:bg-accent/50"
          onClick={triggerFileInput}
        >
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
              <Camera className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Take Photo</h3>
              <p className="text-sm text-muted-foreground">
                Use camera to capture
              </p>
            </div>
          </div>
        </Card>

        {/* Choose from Gallery Option */}
        <Card
          className="cursor-pointer rounded-2xl border-0 p-5 shadow-sm transition-colors hover:bg-accent/50"
          onClick={triggerFileInput}
        >
          <div className="flex items-center gap-4">
            <div className="bg-chart-1/10 flex h-12 w-12 items-center justify-center rounded-xl">
              <Upload className="text-chart-1 h-6 w-6" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">
                Choose from Gallery
              </h3>
              <p className="text-sm text-muted-foreground">
                Select from your photos
              </p>
            </div>
          </div>
        </Card>

        {/* Remove Photo Option */}
        {profileImage && (
          <Card
            className="cursor-pointer rounded-2xl border-0 p-5 shadow-sm transition-colors hover:bg-destructive/10"
            onClick={handleRemovePhoto}
          >
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-destructive/10">
                <X className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <h3 className="font-semibold text-destructive">Remove Photo</h3>
                <p className="text-sm text-muted-foreground">
                  Delete current photo
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Photo Guidelines */}
        <Card className="rounded-2xl border-0 bg-muted/30 p-5 shadow-sm">
          <h3 className="mb-2 font-semibold text-foreground">
            Photo Guidelines
          </h3>
          <ul className="space-y-1 text-sm text-muted-foreground">
            <li>• Use a clear, recent photo</li>
            <li>• Maximum file size: 5MB</li>
            <li>• Recommended: Square format</li>
            <li>• Supported: JPG, PNG, WEBP</li>
          </ul>
        </Card>

        {/* Save Changes Button */}
        <Button onClick={handleSaveChanges} className="h-12 w-full rounded-xl">
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default UploadProfilePhoto;
